import React, {useEffect, useState} from 'react'
import {default as Select} from "react-select"
import 'react-datepicker/dist/react-datepicker.css'
import * as dayjs from 'dayjs'
import * as utc from 'dayjs/plugin/utc'
import * as timezone from 'dayjs/plugin/timezone'
import * as relativeTime from 'dayjs/plugin/relativeTime'
import {REPORTER_USER_LEVEL, useSolv} from "./components/SolvProvider"
import {useIntlEx} from "./components/IntlUtils.js"
import {
  clearAllErrors,
  InfoBlock,
  InfoText,
  validateEmailAddress,
  validateField
} from './components/ValidationUtils.js';
import {closeDialog, ModalDialog} from "./components/DialogUtils";
import {ActionButton, FormGroup, useAlert} from "./components/FormComps";
import {mkHref} from "./components/AccountUtils";
import {useAccessLevelOpts, useThemeOpts} from "./components/UserUtils";
import getLogger from "./components/Logging.js"
import './App.css'

const log = getLogger("MemberInviteDialog")

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(relativeTime)

export default function MemberInviteDialog(props)  {

  const {brandId, api, session:{user}, setBusy} = useSolv()

  const {intl} = useIntlEx()
  const [alert, setAlert] = useAlert()

  const {findAccessLevelOpt, findInvitableAccessLevelOpts} = useAccessLevelOpts()
  const {themeOpts, findThemeOpt} = useThemeOpts()

  const [emailAddress, setEmailAddress] = useState(null)
  const [fullName, setFullName] = useState(null)
  const [userLevel, setUserLevel] = useState(null)
  const [lang, setLang] = useState(null)
  const [theme, setTheme] = useState(null)
  const [oldUserLevel, setOldUserLevel] = useState(null)

  const [accessLevelOpts, setAccessLevelOpts] = useState(null)

  const [touched, setTouched] = useState(null)

  useEffect(() => {
    if (props.tenant) {
      const a = findInvitableAccessLevelOpts(props.tenant, user.userLevel)
      if (a.length === 0) {
        setUserLevel(findAccessLevelOpt(REPORTER_USER_LEVEL))
      }
      else if (a.length >= 1) {
        setAccessLevelOpts(a)
        setUserLevel(a[0])
      }
    }
  }, [props]);


  function handleEmailAddressChange(e) {
    let v = e.target.value
    if (v) {
      v = v.toLowerCase()
    }
    setEmailAddress(v)
    setTouched(true)
  }

  function handleFullNameChange(e) {
    setFullName(e.target?.value)
    setTouched(true)
  }

  function handleUserLevelChange(v) {
    setUserLevel(v)
    setTouched(true)
  }

  function handleLangChange(v) {
    setLang(v)
    setTouched(true)
  }

  function handleThemeChange(v) {
    setTheme(v)
    setTouched(true)
  }


  function validate() {

    let opt = {focusEl: null, valid: true}

    clearAllErrors("frmMain")

    validateField("inp_fullName", fullName && fullName.trim().length > 0, "required", opt)

    validateField("inp_emailAddress", validateEmailAddress(emailAddress), "valid", opt)

    validateField("inp_accessLevel", userLevel, "required", opt)

    if (opt.focusEl) {
      document.getElementById(opt.focusEl).focus()
    }

    return opt.valid

  }

  async function handleInviteClick() {

    try {

      if (validate()) {

        setBusy(intl.msg("working"))

        const payload = {
          fullName: fullName,
          emailAddress: emailAddress,
          accessLevel: userLevel.value,
          invokedById: props.tenant.accessingAs("*/INSTREAM/*") ? user.userId : props.tenant?.owner?.userId
        }

        // log.debug("handleSaveClick: payload=", payload)

        const {data} = await api.insertUser(props.tenant.tenantId, payload)

        // log.debug("handleSaveClick: res=", data)

        window.location = mkHref({suffix: `/members`, tenant: props.tenant, user: user})
      }
      else {
        setAlert({type: "error", text: intl.msg("error_validation")})
      }

    }
    catch (error) {
      log.error("Validation error: ", error)
      handleError(error)
    }
    finally {
      setBusy(null)
    }
  }


  function handleOpenDialog() {
    setAlert(null)
    setTouched(false)
  }

  function handleCloseDialog() {
    closeDialog("dlg_member_invite")
  }

  function handleError(error) {
    if (error && error.code) {
      // log.debug("handleError", error.code)
      const code = error.code
      const details = error.details
      switch (code) {
        case "BANNED_INVITER":
          setAlert({type: "error", text: intl.msg("error_banned_user", details)})
          break
        case "UNKNOWN_INVITER":
        case "INVALID_INVITER":
          setAlert({type: "error", text: intl.msg("invite_error_invalid_inviter", details), field: "inp_emailAddresses", constraint: "valid"})
          break
        case "DUPLICATE_INVITEE_USER":
          setAlert({type: "error", text: intl.msg("invite_error_duplicate_user", details), field: "inp_emailAddresses", constraint: "unique"})
          break
        case "DUPLICATE_INVITEE_TENANT":
          setAlert({type: "error", text: intl.msg("invite_error_duplicate_tenant", details), field: "inp_tenantName", constraint: "unique"})
          break
        case "BANNED_EMAIL_ADDRESS":
          setAlert({type: "error", text: intl.msg("error_banned_email_address", details), field: "inp_emailAddresses", constraint: "valid"})
          break
        default:
          setAlert({type: "error", text: intl.msg("error_failed", details)})
          break
      }
    }
    else {
      setAlert({type: "error", text: intl.msg("error_failed")})
    }
  }

  return (

    props?.tenant &&
      <>

        <ModalDialog
          dialogId="dlg_member_invite"
          size="lg"
          keyboard="true"
          backdrop="static"
          onOpen={handleOpenDialog}
          onClose={handleCloseDialog}>

          <ModalDialog.Header
            title={intl.msg("member_invite_title")}
            description={intl.msg("member_invite_description")}
            alert={alert}
          />

          <ModalDialog.Body>

            <FormGroup>
              <FormGroup.Label htmlFor="inp_fullName" text={intl.msg("member_invite_full_name")} description={intl.msg("member_invite_full_name_description")}/>
              <FormGroup.Controls>
                <input  id="inp_fullName" className="form-control" type="text" value={fullName} autoFocus onChange={handleFullNameChange}/>
                <InfoBlock>
                  <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                </InfoBlock>
              </FormGroup.Controls>
            </FormGroup>

            <FormGroup>
              <FormGroup.Label text={intl.msg(`member_invite_email_address`)} description={intl.msg(`member_invite_email_address_description`)} htmlFor="inp_emailAddress"/>
              <FormGroup.Control>
                <div style={{maxWidth: "500px"}}>
                  <input id="inp_emailAddress" type="text" className="form-control" maxLength={150} value={emailAddress} onChange={handleEmailAddressChange}/>
                </div>
                <InfoBlock>
                  <InfoText validate="unique">{intl.msg("member_email_address_helptext_unique")}</InfoText>
                  <InfoText validate="valid">{intl.msg("helptext_email_address_valid")}</InfoText>
                  <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                </InfoBlock>
              </FormGroup.Control>
            </FormGroup>

            <FormGroup disabled={!(accessLevelOpts?.length > 1)}>
              <FormGroup.Label text={intl.msg("member_access_level")} description={intl.msg("member_access_level_description")} htmlFor="inp_accessLevel"/>
              <FormGroup.Control>
                <div style={{maxWidth: "500px"}}>
                  <Select
                    id="inp_accessLevel"
                    className="react-select"
                    classNamePrefix="react-select"
                    cacheOptions
                    value={userLevel}
                    options={accessLevelOpts}
                    placeholder={null}
                    isSearchable={false}
                    // hideSelectedOptions={true}
                    onChange={handleUserLevelChange}
                  />
                </div>
                <InfoBlock>
                  <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                </InfoBlock>
              </FormGroup.Control>
            </FormGroup>

          </ModalDialog.Body>

          <ModalDialog.Footer>
            <ModalDialog.Footer.Controls>
              <ActionButton name="apply" text={intl.msg("add_new")} flavor="primary" disabled={!emailAddress || !touched} onClick={handleInviteClick}/>
              <ActionButton name="cancel" onClick={handleCloseDialog}/>
            </ModalDialog.Footer.Controls>
          </ModalDialog.Footer>

        </ModalDialog>

      </>
  )
}